// components/CustomerMapModal/MapCanvas.tsx
// Google Maps の描画・ピン・吹き出し・境界変更を担うコンポーネント。
// 参考(customermap.js)の initMap / createIcon / selectIcon / bounds_changed を移植。
// データ取得や状態管理は useCustomerMap が持ち、本コンポーネントは描画と地図イベントに専念する。

import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps, pinIconUrl } from '../../utils/googleMaps';
import { CustomerMapBounds, CustomerMapItem } from '../../types/customerMap';
import { CustomerMapConfig, CustomerMapSelected } from '../../hooks/useCustomerMap';

// 座標重複時の移動間隔（参考: iconDuplicateSpace）
const ICON_DUPLICATE_SPACE = 0.0003;
// 座標重複時のアイコン設定最大数（参考: maxDupeCount）
const MAX_DUPE_COUNT = 10;
// bounds_changed のデバウンス(ms)（参考: 500ms）
const BOUNDS_DEBOUNCE_MS = 500;

export interface MapCanvasProps {
  config: CustomerMapConfig;
  /** 周辺顧客（ピン表示対象） */
  areaList: CustomerMapItem[];
  /** 選択中顧客（中心移動・吹き出し表示） */
  selected: CustomerMapSelected | null;
  /** 表示領域が変わったとき（デバウンス済み） */
  onBoundsChange: (bounds: CustomerMapBounds) => void;
  /** ピンを選択したとき */
  onSelectCustomer: (customerno: number) => void;
  /**
   * 描画リセットの合図（参考: クエリ検索ボタンの icons/makers/area クリア）。
   * 値が変わると既存ピンを全消去してから areaList を描き直す。
   */
  resetToken?: number;
  /**
   * モーダルが表示中か。非表示(display:none)→再表示で Google Maps のタイルが
   * 崩れる（グレーになる）ため、表示に戻ったら resize を発火して中心を保つ。
   * 戻るボタンでの地図復元時に必要。
   */
  active?: boolean;
  /**
   * 初期表示の中心・ズーム。復元表示のとき、離脱時の画角をそのまま再現するために渡す。
   * null のときは従来どおり選択顧客/既定値で中心を決める。
   */
  initialView?: { center: { lat: number; lng: number }; zoom: number } | null;
  /** 表示位置(中心・ズーム)が変わったとき。復元用に hook へ記憶させる。 */
  onViewChange?: (center: { lat: number; lng: number }, zoom: number) => void;
}

const MapCanvas: React.FC<MapCanvasProps> = ({
  config,
  areaList,
  selected,
  onBoundsChange,
  onSelectCustomer,
  resetToken = 0,
  active = true,
  initialView = null,
  onViewChange,
}) => {
  const mapElRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const infoWindowRef = useRef<any>(null);
  // 選択強調マーカー（参考: selectMarker。既定マーカーを選択地点に重ねる）
  const selectMarkerRef = useRef<any>(null);
  // 描画済み customerno
  const drawnNosRef = useRef<Set<number>>(new Set());
  // 既に使った座標（重複ずらし判定用）
  const usedPositionsRef = useRef<{ lat: number; lng: number }[]>([]);
  // customerno → marker
  const markersRef = useRef<Map<number, any>>(new Map());
  // customerno → rankname（選択解除時に元のランク別アイコンへ戻すため保持）
  const markerRankRef = useRef<Map<number, string | undefined>>(new Map());
  // 現在「赤く選択表示」しているピンの customerno（選択切替時に前ピンを元へ戻す）
  const prevSelectedNoRef = useRef<number | null>(null);
  const debounceRef = useRef<any>(null);
  // 直近に処理した resetToken（変化検知用）
  const lastResetTokenRef = useRef<number>(resetToken);
  // 地図が初期化され描画可能になったか（初回ピン/吹き出し描画のトリガ）
  const [mapReady, setMapReady] = useState(false);
  // Google Maps ロード失敗メッセージ（真っ白化を防ぎ、原因を画面に出す）
  const [loadError, setLoadError] = useState<string | null>(null);

  // 最新のコールバックを参照（リスナー内のクロージャ固定化を防ぐ）
  const onBoundsChangeRef = useRef(onBoundsChange);
  const onSelectCustomerRef = useRef(onSelectCustomer);
  const onViewChangeRef = useRef(onViewChange);
  useEffect(() => { onBoundsChangeRef.current = onBoundsChange; }, [onBoundsChange]);
  useEffect(() => { onSelectCustomerRef.current = onSelectCustomer; }, [onSelectCustomer]);
  useEffect(() => { onViewChangeRef.current = onViewChange; }, [onViewChange]);

  // --- 地図初期化（参考: initMap の冒頭 + bounds_changed リスナー） ---
  useEffect(() => {
    let cancelled = false;
    // 地図が出ないときの切り分け用：init応答から受け取った地図設定を出力する。
    // googleMapsApi が空/undefined なら /api2/map/init のレスポンス項目名の不一致が原因。
    console.log('[CustomerMap] 地図初期化 config:', {
      googleMapsApi: config.googleMapsApi,
      hasApiKey: !!config.googleMapsApiKey,
      defaultLat: config.defaultLat,
      defaultLng: config.defaultLng,
    });
    loadGoogleMaps(config.googleMapsApi, config.googleMapsApiKey)
      .then((google) => {
        if (cancelled || !mapElRef.current || mapRef.current) return;

        // 復元表示なら離脱時の画角(initialView)をそのまま使う。無ければ選択顧客/既定値。
        const hasSelf = selected?.latitude != null && selected?.longitude != null;
        const center = initialView
          ? initialView.center
          : hasSelf
          ? { lat: selected!.latitude as number, lng: selected!.longitude as number }
          : { lat: config.defaultLat, lng: config.defaultLng };
        const zoom = initialView
          ? initialView.zoom
          : hasSelf
          ? config.defaultZoom
          : config.defaultZoomNone;

        const map = new google.maps.Map(mapElRef.current, {
          center,
          zoom,
          minZoom: config.minZoom,
          disableDefaultUI: false,
          clickableIcons: false,
          // スマホで一本指パン操作を可能にする（デフォルトは二本指必須）
          gestureHandling: 'greedy',
        });
        mapRef.current = map;
        // disableAutoPan: 吹き出しを画面内に収めようとする InfoWindow 自動パンを無効化。
        // これを有効のままにすると、ピンタップ時に map.panTo() の中心移動と InfoWindow の
        // 自動パンが二重に走り、一瞬横にカクついてから目的地へ動く挙動になる。
        // 中心移動は panTo に一本化して滑らかにする。
        infoWindowRef.current = new google.maps.InfoWindow({ disableAutoPan: true });
        // 地図が使える状態になったことを描画effectへ通知（参考: initMap 内で同期的にピン/吹き出しを描く挙動の再現）
        setMapReady(true);

        // 境界変更 → デバウンスして通知（参考: bounds_changed → repage）
        map.addListener('bounds_changed', () => {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            const b = map.getBounds();
            if (!b) return;
            const ne = b.getNorthEast();
            const sw = b.getSouthWest();
            const lat1 = ne.lat();
            const lat2 = sw.lat();
            if (lat1 === lat2) return; // 取得失敗
            onBoundsChangeRef.current({ lat1, lat2, lng1: ne.lng(), lng2: sw.lng() });
            // 復元用に現在の中心・ズームを記憶させる
            const c = map.getCenter();
            if (c && onViewChangeRef.current) {
              onViewChangeRef.current({ lat: c.lat(), lng: c.lng() }, map.getZoom());
            }
          }, BOUNDS_DEBOUNCE_MS);
        });
      })
      .catch((e) => {
        // 握り潰さずに原因を出す（従来は無言で真っ白になっていた）。
        console.error('[CustomerMap] Google Maps のロードに失敗:', e);
        if (!cancelled) setLoadError(e?.message || 'Google Maps のロードに失敗しました');
      });

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // config 確定時に一度だけ初期化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // --- 周辺顧客のピン描画（参考: createIcon。座標重複はずらす） ---
  useEffect(() => {
    const google = (window as any).google;
    const map = mapRef.current;
    if (!google || !map) return;

    // 描画リセット合図（参考: クエリ検索時の icons/makers/area クリア + initMap）。
    // 既存ピンを全消去し、描画済み・座標の記録もクリアして描き直す。
    if (resetToken !== lastResetTokenRef.current) {
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current.clear();
      markerRankRef.current.clear();
      prevSelectedNoRef.current = null;
      drawnNosRef.current.clear();
      usedPositionsRef.current = [];
      lastResetTokenRef.current = resetToken;
    }

    areaList.forEach((c) => {
      if (c.customerno == null || c.latitude == null || c.longitude == null) return;
      if (drawnNosRef.current.has(c.customerno)) return;

      let lat = c.latitude as number;
      let lng = c.longitude as number;
      let dupe = 0;
      while (usedPositionsRef.current.some((p) => p.lat === lat && p.lng === lng)) {
        if (++dupe >= MAX_DUPE_COUNT) return;
        lng += ICON_DUPLICATE_SPACE;
      }

      const marker = new google.maps.Marker({
        map,
        position: { lat, lng },
        // 参考(createIcon)と同じくランク別のピン画像を使用（A〜G / 既定）。
        icon: {
          url: pinIconUrl(c.rankname),
        },
        zIndex: 1,
      });
      marker.addListener('click', () => {
        onSelectCustomerRef.current(c.customerno);
      });

      drawnNosRef.current.add(c.customerno);
      usedPositionsRef.current.push({ lat, lng });
      markersRef.current.set(c.customerno, marker);
      markerRankRef.current.set(c.customerno, c.rankname);
    });
    // mapReady: 地図初期化完了後に初回描画を確実に行う
  }, [areaList, resetToken, mapReady]);

  // --- 選択顧客へ中心移動＋強調マーカー＋吹き出し（参考: selectIcon） ---
  useEffect(() => {
    const google = (window as any).google;
    const map = mapRef.current;
    const iw = infoWindowRef.current;
    if (!google || !map || !iw || !selected) return;

    // 選択地点の座標を決める。
    // 参考(selectIcon)はクリックしたピンが保持する座標を直接使い、select API の応答に
    // 依存しない。ここでも地図に描画済みのピン(markersRef)があれば、その「実際の描画位置」を
    // 最優先にする。select 応答の座標を優先すると、座標重複ずらし分のズレや、応答に座標が
    // 無いときの既定座標フォールバックにより、タップしたピンとは別の場所へパンしてしまい、
    // タップしたピンが画面外に出ることがあるため（特に右端のピン）。
    // 描画済みピンが無い選択（リストから選んだ圏外顧客等）のみ select 応答→既定座標の順で補う。
    let lat: number | null = null;
    let lng: number | null = null;
    const drawn = markersRef.current.get(selected.customerno);
    const drawnPos = drawn?.getPosition?.();
    if (drawnPos) {
      lat = drawnPos.lat();
      lng = drawnPos.lng();
    }
    if (lat == null || lng == null) {
      lat = selected.latitude ?? null;
      lng = selected.longitude ?? null;
    }
    // それでも座標が得られなければ既定座標へ（参考: selectIcon の isNaN フォールバック）
    if (lat == null || lng == null) {
      lat = config.defaultLat;
      lng = config.defaultLng;
    }
    const position = { lat, lng };

    // 直前に赤く選択表示していたピンを元のランク別アイコンへ戻す（選択は1つだけ赤くする）。
    const prevNo = prevSelectedNoRef.current;
    if (prevNo != null && prevNo !== selected.customerno) {
      const pm = markersRef.current.get(prevNo);
      if (pm) {
        pm.setIcon({ url: pinIconUrl(markerRankRef.current.get(prevNo)) });
        pm.setZIndex(1);
      }
    }
    prevSelectedNoRef.current = selected.customerno;

    // 選択強調の本体。
    // 参考(selectIcon)は「アイコン無し＝Google既定の赤マーカー」で選択地点を示す。
    // ここでは地図上に描画済みのピンなら、そのピン自身のアイコンを赤(既定)へ差し替えて
    // 「タップしたピンが赤くなる」挙動を確実に出す。描画済みピンが無い選択（例: リストから
    // 選んだ圏外顧客や、周辺に含まれない起点顧客）のみ、従来どおり赤マーカーを重ねる。
    // （drawn は座標決定で取得済みのものを再利用）
    let anchorMarker: any;
    if (drawn) {
      // アイコンを外す＝Google既定の赤マーカーになる（参考: selectMarker のアイコン無し）
      drawn.setIcon(null);
      drawn.setZIndex(2);
      anchorMarker = drawn;
      // 以前重ねていたオーバーレイ選択マーカーが残っていれば除去
      if (selectMarkerRef.current) {
        selectMarkerRef.current.setMap(null);
        selectMarkerRef.current = null;
      }
    } else {
      // 既存のオーバーレイを除去し、選択地点に既定（赤）マーカーを重ねて強調（zIndex 2）
      if (selectMarkerRef.current) selectMarkerRef.current.setMap(null);
      const selectMarker = new google.maps.Marker({ map, position, zIndex: 2 });
      selectMarker.addListener('click', () => onSelectCustomerRef.current(selected.customerno));
      selectMarkerRef.current = selectMarker;
      anchorMarker = selectMarker;
    }

    map.panTo(position);

    const company = selected.companyname ? `<div style="font-weight:600">${selected.companyname}</div>` : '';
    iw.setContent(`<div style="min-width:120px">${company}<div>${selected.customername ?? ''}</div></div>`);
    iw.open(map, anchorMarker);
    // mapReady: 地図初期化完了後に初回の選択表示を確実に行う
  }, [selected, mapReady, config.defaultLat, config.defaultLng]);

  // --- 再表示時のタイル崩れ対策（戻るボタンでの地図復元時） ---
  // モーダルが display:none から再表示されると Google Maps のタイルがグレーのまま
  // 崩れることがあるため、active に戻ったら resize を発火して中心を保持する。
  // antd のモーダル開アニメーション後にサイズが確定するよう少し遅延させる。
  useEffect(() => {
    if (!active || !mapReady) return;
    const google = (window as any).google;
    const map = mapRef.current;
    if (!google || !map) return;
    const timer = setTimeout(() => {
      const center = map.getCenter();
      google.maps.event.trigger(map, 'resize');
      if (center) map.setCenter(center);
    }, 350);
    return () => clearTimeout(timer);
  }, [active, mapReady]);

  return (
    <div className="cmm-map-canvas-wrap">
      <div ref={mapElRef} className="cmm-map-canvas" />
      {loadError && (
        <div className="cmm-map-loaderror">
          <div>地図を読み込めませんでした</div>
          <div className="cmm-map-loaderror-detail">{loadError}</div>
        </div>
      )}
    </div>
  );
};

export default MapCanvas;
